/**
 * Navidium Cart Protection Integration Script
 *
 * This script handles the integration of Navidium's product protection offerings
 * within the Shopify cart. It manages two main workflows:
 *
 * 1. Cart Integration:
 * - Injects the Navidium protection widget into the cart
 * - Handles cart updates and re-initialization of the widget
 * - Adds fallback styling when Navidium is not available
 *
 * 2. Checkout Flow Management:
 * - Manages two checkout paths:
 *   a) With protection (nvd-with-checkout-btn)
 *   b) Without protection (nvd-without-checkout-btn)
 *
 * Fallback Behavior:
 * - When Navidium is unavailable (nvd_init not found):
 *   - Adds 'no-nvidium' class to the original Shopify checkout button
 *   - Ensures default checkout flow remains functional
 *   - Maintains visibility of the original checkout button
 *   - Prevents broken UI states in case of script loading issues
 *
 * Key Components:
 * - injectNvdToCart: Handles DOM injection of the Navidium widget
 * - handleCartChange: Re-initializes widget on cart updates
 * - handleProtectedCheckout: Processes checkouts with protection
 * - handleUnprotectedCheckout: Manages checkouts without protection
 * - addNoNvidiumClass: Provides fallback styling and ensures checkout button visibility
 *
 * Implementation Notes:
 * - Uses event delegation for checkout button clicks to handle dynamically added elements
 * - Implements defensive programming with null checks and fallbacks
 * - Relies on external nvd_init function provided by Navidium's main script
 * - Uses localStorage to maintain protection selection state
 *
 * Event Listeners:
 * - DOMContentLoaded: Initial setup
 * - baseline:cart:open/update: Cart modification handling
 * - theme:cart:change: Cart content changes
 * - click: Checkout button interactions
 */

window.needToInjectNvd = true;

const CHECKOUT_SELECTORS = {
  withProtection: "#nvd-with-checkout-btn, #nvd-with-checkout-btn *",
  withoutProtection: "#nvd-without-checkout-btn, #nvd-without-checkout-btn *",
};

const injectNvdToCart = () => {
  const parent = document.querySelector(".js-mini-container");
  const nvdContainer = document.querySelector(".trigger-checkout");

  if (typeof nvd_init !== "function") {
    addNoNvidiumClass();
  }

  if (!nvdContainer) return;

  const nvdDiv = document.createElement("div");
  nvdDiv.setAttribute("class", "nvd-mini w-nvd-100 trigger-checkout");

  if (parent && !parent.innerHTML.includes("nvd-mini")) {
    nvdContainer.parentNode.insertBefore(nvdDiv, nvdContainer);
  }
};

const handleCartChange = (e) => {
  if (typeof nvd_init !== "function") {
    return;
  }

  if (e.detail?.type === "theme:line-item:change") {
    nvd_init();
  }
};

const handleProtectedCheckout = (e) => {
  e.preventDefault();

  localStorage.getItem("nvdVariant")
    ? nvdAddProtectionController()
    : (window.location.href = `${window.location.origin}/checkout`);
};

const handleUnprotectedCheckout = (e) => {
  e.preventDefault();

  const reminderModal = document.querySelector("#nvd-reminder-modal");
  reminderModal?.innerHTML?.length
    ? nvdReminderPopupOpen()
    : (window.location.href = `${window.location.origin}/checkout`);
};

const addNoNvidiumClass = () => {
  const checkoutButton = document.querySelector(".js-footer-checkout-button");
  const nvidiumMini = document.querySelector(".nvd-mini");

  if (checkoutButton) {
    checkoutButton.classList.add("no-nvidium");
    nvidiumMini?.classList.add("no-nvidium");
  }
};

document.addEventListener("DOMContentLoaded", injectNvdToCart);
document.addEventListener("baseline:cart:open", injectNvdToCart);
document.addEventListener("baseline:cart:update", injectNvdToCart);
document.addEventListener("theme:cart:change", handleCartChange);

window.addEventListener(
  "click",
  (e) => {
    const withProtectionTrigger = Array.from(
      document.querySelectorAll(CHECKOUT_SELECTORS.withProtection)
    );
    if (withProtectionTrigger.includes(e.target)) handleProtectedCheckout(e);
  },
  true
);
window.addEventListener(
  "click",
  (e) => {
    const withoutProtectionTrigger = Array.from(
      document.querySelectorAll(CHECKOUT_SELECTORS.withoutProtection)
    );
    if (withoutProtectionTrigger.includes(e.target))
      handleUnprotectedCheckout(e);
  },
  true
);
